class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', theme.utils.sectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const translateY = theme.config.motionReduced ? 0 : 50;

    Motion.timeline([
      [items, { y: translateY, opacity: 0, visibility: 'hidden' }, { duration: 0.5, delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }],
      [container, { y: translateY, opacity: 0 }, { duration: 0.5, easing: 'linear' }],
    ]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20),
        behavior: theme.config.motionReduced ? 'auto' : 'smooth'
      });

      const drawer = document.getElementById('FacetDrawer');
      if (drawer) drawer.classList.add('loading');
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const translateY = theme.config.motionReduced ? 0 : 50;

    Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }, { duration: 0.5, easing: 'linear' }],
      [items, { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.5, delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }],
    ]);

    const drawer = document.getElementById('FacetDrawer');
    if (drawer) drawer.classList.remove('loading');

    document.dispatchEvent(new CustomEvent('collection:reloaded'));
  }

  renderSection(url, event) {
    // What is on screen now, to go back to if no usable answer comes.
    if (!FacetForm.shown) FacetForm.shown = { url: window.location.href, text: null };
    FacetForm.wanted = url;

    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url, event)
      : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    
    this.beforeRenderSection();
    this.fetchSection(url, event, this.abortController.signal, performance.now(), true);
  }

  fetchSection(url, event, signal, start, firstTry) {
    // A rate limit, a server error or a dropped connection is usually over
    // in a moment, so the request is made once more before giving up. If
    // that fails as well, the page goes back to what it was showing. Neither
    // happens once the shopper has chosen something else in the meantime.
    const noAnswer = (wait) => {
      setTimeout(() => {
        if (signal.aborted || FacetForm.wanted !== url) return;
        firstTry ? this.fetchSection(url, event, signal, start, false) : this.showPrevious();
      }, firstTry ? wait : 0);
    };

    fetch(url, { signal: signal })
      .then((response) => response.text().then((responseText) => {
        const problem = this.answerProblem(url, response, responseText);

        if (problem === null) {
          const execution = (performance.now() - start);

          setTimeout(() => {
            this.renderResponse(responseText, event);
            this.cachedMap.set(url, responseText);
            FacetForm.shown = { url: this.pageUrl(url), text: responseText };
          }, execution > 500 ? 0 : 500);
        }
        else if (problem === 'busy') {
          const seconds = parseFloat(response.headers.get('Retry-After'));
          noAnswer(Math.min(Math.max(seconds * 1000 || 1500, 1000), 3000));
        }
        else {
          // Not the results, and asking again would not change that. Load
          // the address as an ordinary page instead, so the shopper sees
          // what the server sends for it. Nothing on a fresh page asks
          // again by itself, so it cannot loop.
          this.report(problem, response, responseText, () => this.loadPage(url));
        }
      }))
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        }
        else {
          console.error(error);
          noAnswer(1500);
        }
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderResponse(responseText, event);
      FacetForm.shown = { url: this.pageUrl(url), text: responseText };
    }, 500);
  }

  // Each block is swapped on its own, so one that fails cannot leave old
  // products under a new address, and the results always fade back in. The
  // first failure is thrown afterwards so that it still reaches Sentry.
  renderResponse(responseText, event) {
    let failure;
    const attempt = (step) => {
      try { step(); } catch (error) { failure = failure || error; }
    };

    attempt(() => this.renderFilters(responseText, event));
    attempt(() => this.renderFiltersActive(responseText));
    attempt(() => this.renderProductGridContainer(responseText));
    attempt(() => this.renderProductCount(responseText));
    attempt(() => this.renderSortBy(responseText));
    attempt(() => theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText }));

    this.afterRenderSection();
    if (failure) throw failure;
  }

  // Says why an answer cannot be swapped in, or null when it can. The
  // product grid and the active filters are sent with every answer for a
  // page that has them (a search with no matches still has both), so one
  // of them missing means the answer is not the results. THEME-27 was a 200
  // without the active filters.
  answerProblem(url, response, responseText) {
    if (!response.ok) {
      return [408, 429, 430].includes(response.status) || response.status >= 500 ? 'busy' : 'answer was an error';
    }
    if (response.redirected && new URL(response.url).pathname !== new URL(url).pathname) {
      return 'answer was for another address';
    }

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const missing = (id) => document.getElementById(id) !== null && parsedHTML.getElementById(id) === null;
    if (missing('ProductGridContainer')) return 'answer had no results in it';
    if (missing('FacetFiltersActive')) return 'answer had no active filters in it';
    return null;
  }

  pageUrl(url) {
    const pageUrl = new URL(url);
    pageUrl.searchParams.delete('section_id');
    return pageUrl.toString();
  }

  // Loads the latest address asked for, in case another filter was chosen
  // while a report was on its way.
  loadPage(url) {
    const pageUrl = new URL(this.pageUrl(FacetForm.wanted || url));
    pageUrl.hash = '';
    window.location.replace(pageUrl.toString());
  }

  // No usable answer came. The page goes back to what it was showing, with
  // its address and its controls, so that a filter never looks applied to
  // results it was not applied to.
  showPrevious() {
    const shown = FacetForm.shown;
    if (this.hasAttribute('data-history')) history.replaceState({}, '', shown.url);

    if (shown.text !== null) {
      this.renderResponse(shown.text);
      return;
    }

    // Nothing has been swapped in yet, so the results are still the page's
    // own and only the controls need putting back to how they were sent.
    try {
      document.querySelectorAll('form[is="facet-form"]').forEach((form) => form.reset());
      document.querySelectorAll('price-range, facet-sort').forEach((element) => {
        if (typeof element.restore === 'function') element.restore();
      });
    }
    finally {
      this.afterRenderSection();
    }
  }

  // An answer that is not the results is told to Sentry once per visit, with
  // what came back in its place. The Sentry script only loads when it has
  // something to send, so `then` (loading the address) waits for the
  // message to go, a second and a half at most; a blocked or slow Sentry
  // never holds the page. Rate limits, server errors and dropped
  // connections are not reported: the Sentry key is capped at a few events
  // an hour.
  report(problem, response, responseText, then) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      then();
    };

    try {
      const loader = window.Sentry;
      if (this.reportedBefore() || !loader || typeof loader.captureMessage !== 'function') {
        finish();
        return;
      }

      const extra = {
        status: response.status,
        redirected: !!response.redirected,
        path: new URL(response.url || window.location.href).pathname,
        contentType: (response.headers.get('Content-Type') || '').slice(0, 80),
        length: responseText.length,
        start: responseText.slice(0, 60).replace(/[^\x20-\x7E]+/g, ' ').trim(),
      };
      const send = () => {
        try {
          window.Sentry.captureMessage('Waltons filters: ' + problem, { extra: extra });
          window.Sentry.flush(1500).then(finish, finish);
        } catch (error) { finish(); }
      };

      setTimeout(finish, 1500);
      if (typeof loader.onLoad === 'function') loader.onLoad(send); else send();
      if (typeof loader.forceLoad === 'function') loader.forceLoad();
    } catch (error) { finish(); /* monitoring must never break the page */ }
  }

  reportedBefore() {
    if (FacetForm.reported) return true;
    FacetForm.reported = true;
    try {
      if (sessionStorage.getItem('waltons-filters-reported')) return true;
      sessionStorage.setItem('waltons-filters-reported', '1');
    } catch (error) { /* no session storage: once per page */ }
    return false;
  }

  swapInnerHTML(parsedHTML, id) {
    const target = document.getElementById(id);
    const source = parsedHTML.getElementById(id);
    if (target && source) target.innerHTML = source.innerHTML;
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
    );

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.getAttribute('data-index') === jsFilter.getAttribute('data-index') : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.getAttribute('data-index')}"]`);
      if (filter !== null) {
        // A group that cannot narrow the results renders hidden (facets.liquid);
        // follow it both ways, so it goes or comes back as other ticks change.
        filter.hidden = element.hidden;

        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        }
        else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    this.swapInnerHTML(parsedHTML, id);
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    // A filter with no matches renders the empty state, with no motion-list.
    const list = parsedHTML.querySelector('motion-list');
    if (list) list.setAttribute('motion-reduced', '');

    this.swapInnerHTML(parsedHTML, id);
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    this.swapInnerHTML(parsedHTML, id);
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    this.swapInnerHTML(parsedHTML, id);
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, this.onFacetUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText);
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    if (!facetCount) return;
    this.innerText = facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class FacetRemove extends (customElements.get('magnet-link') || HTMLAnchorElement) {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', theme.utils.sectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}
customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetSort extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });

    this.addEventListener('change', this.onChange);
    this.button?.addEventListener('click', this.show.bind(this));
    this.close?.addEventListener('click', this.hide.bind(this));
    document.addEventListener('click', this.onWindowClick.bind(this));
  }

  get listbox() {
    return this.querySelector('.sort-listbox');
  }

  get selection() {
    return this.querySelector('.sort-selection');
  }

  get button() {
    return this.querySelector('.sort-by');
  }

  get close() {
    return this.querySelector('.sort-close');
  }

  init() {
    this.initButton();
    this.style.setProperty('--facet-listbox-height', `${this.listbox.getBoundingClientRect().height}px`);
  }

  initButton() {
    const value = this.selection.innerText;
    const width = theme.getElementWidth(this.selection, value);
    this.style.setProperty('--facet-button-width', `${width}px`);
  }

  onChange(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', theme.utils.sectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }

    this.selection.innerText = event.target.nextElementSibling.innerText;
    this.initButton();
    this.hide();
  }

  // Puts the choice back to the one the page was sent with, after a sort
  // that got no answer.
  restore() {
    this.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      input.checked = input.defaultChecked;
    });

    // textContent, because the closed list is hidden and innerText skips
    // hidden text.
    const current = this.querySelector('input[name="sort_by"]:checked');
    if (current && current.nextElementSibling && this.selection) {
      this.selection.innerText = current.nextElementSibling.textContent.trim();
      this.initButton();
    }
  }

  show() {
    this.button.setAttribute('open', '');
  }

  hide(immediate = true) {
    if (this.button.hasAttribute('open')) {
      setTimeout(() => {
        this.button.removeAttribute('open');
      }, 100);

      if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;
      
      const btnFill = this.button.querySelector('[data-fill');
      Motion.animate(btnFill, { y: ['0%', immediate ? '0%' : '-76%'] }, { duration: 0.6, delay: immediate ? 0 : 0.2 });
    }
  }

  onWindowClick(event) {
    if (!this.contains(event.target)) {
      if (this.button.hasAttribute('open')) {
        this.hide(false);
      }
    }
  }
}
customElements.define('facet-sort', FacetSort);

class FacetSticky extends HTMLElement {
  constructor() {
    super();

    // Some in-app browsers (iOS 16.3 in Sentry) have no IntersectionObserver
    // and this threw while the page was still starting up. Without it the
    // button stays where the stylesheet puts it and the filter drawer still
    // opens; only the show and hide on scroll is lost.
    if (typeof window.IntersectionObserver === 'undefined') return;

    // Some crawlers and embedded browsers report a screen height of 0, and
    // anything under 100 made the top margin "--100px", which is not a
    // margin: the observer threw and the button never hid on scroll. Use
    // the window height when there is no screen height, and stop at zero.
    let height = Number(screen.availHeight);
    if (!(height > 0)) height = Number(window.innerHeight);
    height = height > 0 && Number.isFinite(height) ? Math.round(height) : 0;

    new IntersectionObserver(this.handleIntersection.bind(this), { rootMargin: `${-Math.max(height - 100, 0)}px 0px ${height}px 0px` }).observe(document.querySelector('.collection-section'));
  }

  get button() {
    return this.querySelector('.button');
  }

  handleIntersection(entries) {
    if (entries[0].isIntersecting) {
      Motion.animate(this.button, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
    else {
      Motion.animate(this.button, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
  }
}
customElements.define('facet-sticky', FacetSticky);

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector('input[type="range"]:first-child');
    this.rangeMax = this.querySelector('input[type="range"]:last-child');
    this.inputMin = this.querySelector('input[name="filter.v.price.gte"]');
    this.inputMax = this.querySelector('input[name="filter.v.price.lte"]');

    // Facet re-renders can produce a price-range without its inputs
    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax) return;

    this.inputMin.addEventListener('focus', this.inputMin.select);
    this.inputMax.addEventListener('focus', this.inputMax.select);
    this.inputMin.addEventListener('change', this.onInputMinChange.bind(this));
    this.inputMax.addEventListener('change', this.onInputMaxChange.bind(this));

    this.rangeMin.addEventListener('change', this.onRangeMinChange.bind(this));
    this.rangeMax.addEventListener('change', this.onRangeMaxChange.bind(this));
    this.rangeMin.addEventListener('input', this.onRangeMinInput.bind(this));
    this.rangeMax.addEventListener('input', this.onRangeMaxInput.bind(this));
  }

  // Puts the bar between the handles back in step with their values, after
  // the form has been reset.
  restore() {
    if (!this.rangeMin || !this.rangeMax) return;
    const track = this.rangeMin.parentElement;
    track.style.setProperty('--range-min', `${parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max) * 100}%`);
    track.style.setProperty('--range-max', `${parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max) * 100}%`);
  }

  onInputMinChange(event) {
    event.preventDefault();
    event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1), event.target.min);
    this.rangeMin.value = event.target.value;
    this.rangeMin.parentElement.style.setProperty('--range-min', `${parseInt(this.rangeMin.value) / parseInt(this.rangeMin.max) * 100}%`);
  }

  onInputMaxChange(event) {
    event.preventDefault();
    event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1), event.target.max);
    this.rangeMax.value = event.target.value;
    this.rangeMax.parentElement.style.setProperty('--range-max', `${parseInt(this.rangeMax.value) / parseInt(this.rangeMax.max) * 100}%`);
  }

  onRangeMinChange(event) {
    event.stopPropagation();
    this.inputMin.value = event.target.value;
    this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRangeMaxChange(event) {
    event.stopPropagation();
    this.inputMax.value = event.target.value;
    this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRangeMinInput(event) {
    event.target.value = Math.min(parseInt(event.target.value), parseInt(this.inputMax.value || event.target.max) - 1);
    event.target.parentElement.style.setProperty('--range-min', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
    this.inputMin.value = event.target.value;
  }

  onRangeMaxInput(event) {
    event.target.value = Math.max(parseInt(event.target.value), parseInt(this.inputMin.value || event.target.min) + 1);
    event.target.parentElement.style.setProperty('--range-max', `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
    this.inputMax.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class InfiniteButton extends (customElements.get('hover-button') || HTMLButtonElement) {
  constructor() {
    super();

    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('mode') == 'infinite') {
      Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.hasAttribute('aria-busy')) return;
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.enableLoading();
    const url = this.buildUrl().toString();

    fetch(url, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        }
        else {
          console.error(error);
        }
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      destination.innerHTML = source.innerHTML;
    }
    else {
      destination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = productGridContainer.querySelector('motion-list');
    const source = parsedHTML.querySelector('motion-list');

    if (source && destination) {
      source.querySelectorAll('.card').forEach((item) => {
        destination.appendChild(item);
      });

      destination.reload();
    }
  }
  
  buildUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', theme.utils.sectionId(this));
    return url;
  }

  enableLoading() {
    this.classList.add('pointer-events-none');
    this.setAttribute('aria-busy', 'true');
  }
}
customElements.define('infinite-button', InfiniteButton, { extends: 'button' });
